import { APIDelete, APIGet, APIPost, APIPut, APIUrl, AdminType } from "api/protocols";
import type { CancelToken } from "axios";
import type {
  IEmailMarketingTemplateExport,
  IEmailMarketingTemplateList,
  IEmailMarketingTemplatePreview,
  IEmailMarketingTemplateWorkspace,
  IGetEmailMarketingTemplatesParams,
  IPostEmailMarketingTemplateActivateParams,
  IPostEmailMarketingTemplateBlankParams,
  IPostEmailMarketingTemplateDraftDiscardParams,
  IPostEmailMarketingTemplateParams,
  IPutEmailMarketingTemplateDraftParams,
  IPutEmailMarketingTemplateNameParams,
} from "./emailMarketingTemplatesTypes";

export type {
  IEmailMarketingTemplateExport,
  IEmailMarketingTemplateList,
  IEmailMarketingTemplateListItem,
  IEmailMarketingTemplatePreview,
  IEmailMarketingTemplateWorkspace,
  IGetEmailMarketingTemplatesParams,
  IPostEmailMarketingTemplateActivateParams,
  IPostEmailMarketingTemplateBlankParams,
  IPostEmailMarketingTemplateDraftDiscardParams,
  IPostEmailMarketingTemplateParams,
  IPutEmailMarketingTemplateDraftParams,
  IPutEmailMarketingTemplateNameParams,
  TEmailMarketingTemplateScope,
} from "./emailMarketingTemplatesTypes";

function emailMarketingTemplatesUrl(): string {
  return APIUrl(AdminType.FacilityAdmin, "/marketing/email-templates");
}

function emailMarketingTemplateBlankUrl(): string {
  return APIUrl(AdminType.FacilityAdmin, "/marketing/email-templates/blank");
}

function emailMarketingTemplateUrl(templateId: number): string {
  return APIUrl(AdminType.FacilityAdmin, `/marketing/email-templates/${templateId}`);
}

function emailMarketingTemplateDraftUrl(templateId: number): string {
  return APIUrl(AdminType.FacilityAdmin, `/marketing/email-templates/${templateId}/draft`);
}

function emailMarketingTemplateActivateUrl(templateId: number): string {
  return APIUrl(AdminType.FacilityAdmin, `/marketing/email-templates/${templateId}/activate`);
}

function emailMarketingTemplateDraftDiscardUrl(templateId: number): string {
  return APIUrl(
    AdminType.FacilityAdmin,
    `/marketing/email-templates/${templateId}/draft/discard`,
  );
}

function emailMarketingTemplatePreviewUrl(templateId: number): string {
  return APIUrl(AdminType.FacilityAdmin, `/marketing/email-templates/${templateId}/preview`);
}

function emailMarketingTemplateExportUrl(templateId: number): string {
  return APIUrl(AdminType.FacilityAdmin, `/marketing/email-templates/${templateId}/export`);
}

function emailMarketingTemplateNameUrl(templateId: number): string {
  return APIUrl(AdminType.FacilityAdmin, `/marketing/email-templates/${templateId}/name`);
}

function emailMarketingTemplateDuplicateUrl(templateId: number): string {
  return APIUrl(AdminType.FacilityAdmin, `/marketing/email-templates/${templateId}/duplicate`);
}

export async function GetEmailMarketingTemplates(
  params: IGetEmailMarketingTemplatesParams = {},
  useGlobalLoader: boolean = false,
) {
  return await APIGet<IEmailMarketingTemplateList>(emailMarketingTemplatesUrl(), params, useGlobalLoader);
}

export async function GetEmailMarketingTemplate(templateId: number, useGlobalLoader: boolean = false) {
  return await APIGet<IEmailMarketingTemplateWorkspace>(
    emailMarketingTemplateUrl(templateId),
    {},
    useGlobalLoader,
  );
}

export async function PostEmailMarketingTemplate(
  params: IPostEmailMarketingTemplateParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailMarketingTemplateWorkspace }>(
    emailMarketingTemplatesUrl(),
    params,
    useGlobalLoader,
  );
}

export async function PostEmailMarketingTemplateBlank(
  params: IPostEmailMarketingTemplateBlankParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailMarketingTemplateWorkspace }>(
    emailMarketingTemplateBlankUrl(),
    params,
    useGlobalLoader,
  );
}

export async function PutEmailMarketingTemplateDraft(
  templateId: number,
  params: IPutEmailMarketingTemplateDraftParams,
  useGlobalLoader: boolean = false,
  cancelToken?: CancelToken,
) {
  return await APIPut<IEmailMarketingTemplateWorkspace>(
    emailMarketingTemplateDraftUrl(templateId),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

export async function PostEmailMarketingTemplateActivate(
  templateId: number,
  params: IPostEmailMarketingTemplateActivateParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailMarketingTemplateWorkspace }>(
    emailMarketingTemplateActivateUrl(templateId),
    params,
    useGlobalLoader,
  );
}

export async function PostEmailMarketingTemplateDraftDiscard(
  templateId: number,
  params: IPostEmailMarketingTemplateDraftDiscardParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailMarketingTemplateWorkspace }>(
    emailMarketingTemplateDraftDiscardUrl(templateId),
    params,
    useGlobalLoader,
  );
}

export async function PostEmailMarketingTemplatePreview(
  templateId: number,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailMarketingTemplatePreview }>(
    emailMarketingTemplatePreviewUrl(templateId),
    {},
    useGlobalLoader,
  );
}

export async function PostEmailMarketingTemplateExport(
  templateId: number,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailMarketingTemplateExport }>(
    emailMarketingTemplateExportUrl(templateId),
    {},
    useGlobalLoader,
  );
}

export async function PutEmailMarketingTemplateName(
  templateId: number,
  params: IPutEmailMarketingTemplateNameParams,
  useGlobalLoader: boolean = false,
) {
  return await APIPut<IEmailMarketingTemplateWorkspace>(
    emailMarketingTemplateNameUrl(templateId),
    params,
    useGlobalLoader,
  );
}

export async function PostEmailMarketingTemplateDuplicate(
  templateId: number,
  useGlobalLoader: boolean = false,
) {
  return await APIPost<{ data: IEmailMarketingTemplateWorkspace }>(
    emailMarketingTemplateDuplicateUrl(templateId),
    {},
    useGlobalLoader,
  );
}

export async function DeleteEmailMarketingTemplate(
  templateId: number,
  useGlobalLoader: boolean = false,
) {
  return await APIDelete(emailMarketingTemplateUrl(templateId), {}, useGlobalLoader);
}
